import { useEffect, useMemo, useState } from "react";
import SmsConsentNote from "../../components/SmsConsentNote";
import { Button, Card, Empty, Input, Modal, Radio, Result, Select, Space, Spin, Tag, Typography, message } from "antd";
import { CheckCheck, Clock, CreditCard, Monitor, Play, Scissors, Users, XCircle } from "lucide-react";
import dayjs from "dayjs";
import axiosClient from "../../api/axiosClient";

const { Title, Text } = Typography;

interface Walkin {
  walkin_id: string;
  client_name: string;
  client_phone: string;
  service_name: string;
  staff_id?: string | null;
  status: "waiting" | "booked" | "in_service" | "served";
  booking_id?: string | null;
  checked_in_at: string;
}

interface Service { service_id: string; name: string; duration_min: number; price: number; }
interface Staff { user_id: string; name: string; }
interface Client { client_id: string; name: string; phone: string; }

const STATUS_META: Record<Walkin["status"], { label: string; color: string }> = {
  waiting: { label: "Waiting", color: "gold" },
  booked: { label: "Booked", color: "purple" },
  in_service: { label: "In Service", color: "blue" },
  served: { label: "Served", color: "green" },
};

function normalizePhone(v: string) {
  return v.replace(/\D/g, "");
}

// ── Kiosk check-in flow (staff-operated tablet, or handed to the customer) ──
type KioskStep = "phone" | "name" | "service" | "choice" | "book" | "done";

function KioskFlow({ businessId, services, staff, clients, onExit, onCheckedIn }: {
  businessId: string;
  services: Service[];
  staff: Staff[];
  clients: Client[];
  onExit: () => void;
  onCheckedIn: () => void;
}) {
  const [step, setStep] = useState<KioskStep>("phone");
  const [phone, setPhone] = useState("");
  const [matchedClient, setMatchedClient] = useState<Client | null>(null);
  const [nameInput, setNameInput] = useState("");
  const [service, setService] = useState<Service | null>(null);
  const [walkinId, setWalkinId] = useState<string | null>(null);
  const [reserved, setReserved] = useState(false);
  const [bookStaffId, setBookStaffId] = useState<string | null>(null);
  const [bookDate, setBookDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [slots, setSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [pickedSlot, setPickedSlot] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const clientName = matchedClient?.name ?? nameInput;

  function submitPhone() {
    const digits = normalizePhone(phone);
    const found = clients.find((c) => normalizePhone(c.phone).slice(-10) === digits.slice(-10));
    if (found) {
      setMatchedClient(found);
      setStep("service");
    } else {
      setMatchedClient(null);
      setStep("name");
    }
  }

  async function checkInAndWait() {
    if (!service) return;
    setSubmitting(true);
    try {
      const res = await axiosClient.post("/walkins", {
        client_name: clientName,
        client_phone: phone,
        service_id: service.service_id,
        service_name: service.name,
      });
      setWalkinId(res.data.walkin_id);
      setStep("done");
      onCheckedIn();
    } catch {
      message.error("Check-in failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function checkInThenBook() {
    if (!service) return null;
    if (walkinId) return walkinId;
    const res = await axiosClient.post("/walkins", {
      client_name: clientName,
      client_phone: phone,
      service_id: service.service_id,
      service_name: service.name,
    });
    setWalkinId(res.data.walkin_id);
    return res.data.walkin_id as string;
  }

  async function loadSlots() {
    if (!bookStaffId || !service) return;
    setSlotsLoading(true);
    setPickedSlot(null);
    try {
      const res = await axiosClient.get("/public/availability", {
        params: { business_id: businessId, service_id: service.service_id, staff_id: bookStaffId, date: bookDate },
      });
      setSlots(res.data.slots ?? []);
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }

  useEffect(() => {
    if (step === "book" && bookStaffId) loadSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, bookStaffId, bookDate]);

  async function confirmBooking() {
    if (!pickedSlot || !service || !bookStaffId) return;
    setSubmitting(true);
    try {
      const wid = await checkInThenBook();
      const start = pickedSlot;
      const end = dayjs(start).add(service.duration_min, "minute").toISOString();
      await axiosClient.post(`/walkins/${wid}/book`, {
        staff_id: bookStaffId,
        service_id: service.service_id,
        service: service.name,
        start_time: start,
        end_time: end,
        price: service.price,
        client_name: clientName,
        client_phone: phone,
      });
      // Prepaying by card is disabled until a real card processor is connected —
      // the booking is real; the client pays at the appointment.
      setReserved(true);
      setStep("done");
      onCheckedIn();
    } catch (err: any) {
      message.error(err?.response?.data ?? "Failed to book — that slot may no longer be available");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onCancel={onExit} footer={null} width={480} title="Walk-In Check-In" maskClosable={false}>
      {step === "phone" && (
        <Space direction="vertical" size={16} style={{ width: "100%", marginTop: 8 }}>
          <Text>Customer's mobile number</Text>
          <Input size="large" placeholder="(555) 000-0000" value={phone} onChange={(e) => setPhone(e.target.value)} onPressEnter={submitPhone} autoFocus />
          <SmsConsentNote style={{ margin: 0 }} />
          <Button type="primary" block size="large" disabled={normalizePhone(phone).length < 10} onClick={submitPhone}>Next</Button>
        </Space>
      )}

      {step === "name" && (
        <Space direction="vertical" size={16} style={{ width: "100%", marginTop: 8 }}>
          <Text>We don't have this number on file — what's your name?</Text>
          <Input size="large" placeholder="First & last name" value={nameInput} onChange={(e) => setNameInput(e.target.value)} onPressEnter={() => nameInput.trim() && setStep("service")} autoFocus />
          <Button type="primary" block size="large" disabled={!nameInput.trim()} onClick={() => setStep("service")}>Continue</Button>
        </Space>
      )}

      {step === "service" && (
        <Space direction="vertical" size={12} style={{ width: "100%", marginTop: 8 }}>
          <Text>Hi {clientName.split(" ")[0]}! What can we do for you today?</Text>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {services.map((s) => (
              <Card key={s.service_id} size="small" hoverable onClick={() => { setService(s); setStep("choice"); }} style={{ cursor: "pointer" }}>
                <div style={{ fontWeight: 600 }}>{s.name}</div>
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>{s.duration_min} min · ${s.price}</div>
              </Card>
            ))}
          </div>
        </Space>
      )}

      {step === "choice" && (
        <Space direction="vertical" size={16} style={{ width: "100%", marginTop: 8 }}>
          <Text>{service?.name} — how would you like to proceed?</Text>
          <Button size="large" block icon={<Clock size={16} />} onClick={checkInAndWait} loading={submitting}>
            Wait Now — take a seat
          </Button>
          <Button size="large" block icon={<CreditCard size={16} />} onClick={() => setStep("book")}>
            Book & Return — reserve a time
          </Button>
        </Space>
      )}

      {step === "book" && (
        <Space direction="vertical" size={12} style={{ width: "100%", marginTop: 8 }}>
          <Select
            placeholder="Choose a stylist"
            style={{ width: "100%" }}
            value={bookStaffId}
            onChange={setBookStaffId}
            options={staff.map((s) => ({ value: s.user_id, label: s.name }))}
          />
          <Radio.Group value={bookDate} onChange={(e) => setBookDate(e.target.value)}>
            <Radio.Button value={dayjs().format("YYYY-MM-DD")}>Today</Radio.Button>
            <Radio.Button value={dayjs().add(1, "day").format("YYYY-MM-DD")}>Tomorrow</Radio.Button>
          </Radio.Group>
          {slotsLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>
          ) : !bookStaffId ? (
            <Empty description="Pick a stylist to see open times" />
          ) : slots.length === 0 ? (
            <Empty description="No open slots — try another day" />
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, maxHeight: 200, overflowY: "auto" }}>
              {slots.map((s) => (
                <Button key={s} type={pickedSlot === s ? "primary" : "default"} onClick={() => setPickedSlot(s)}>
                  {dayjs(s).format("h:mm A")}
                </Button>
              ))}
            </div>
          )}
          <Button type="primary" block size="large" disabled={!pickedSlot} loading={submitting} onClick={confirmBooking}>
            Reserve this time
          </Button>
        </Space>
      )}

      {step === "done" && (
        <Result
          status="success"
          title="You're all set!"
          subTitle={reserved ? `See you ${dayjs(pickedSlot).format("ddd [at] h:mm A")}! You'll pay at your appointment.` : "We'll call you when it's your turn."}
          extra={<Button type="primary" onClick={onExit}>Done</Button>}
        />
      )}
    </Modal>
  );
}

// ── Staff-facing live queue ──────────────────────────────────────────────
export default function AdminWalkInKioskPage() {
  const [walkins, setWalkins] = useState<Walkin[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [kioskOpen, setKioskOpen] = useState(false);

  function fetchQueue() {
    axiosClient.get("/walkins").then((res) => setWalkins(res.data ?? [])).catch(() => {});
  }

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axiosClient.get("/walkins"),
      axiosClient.get("/settings/services"),
      axiosClient.get("/staff"),
      axiosClient.get("/clients"),
      axiosClient.get("/business"),
    ])
      .then(([w, s, st, c, b]) => {
        setWalkins(w.data ?? []);
        setServices((s.data ?? []).filter((x: any) => x.active !== false));
        setStaff(st.data ?? []);
        setClients(c.data ?? []);
        setBusinessId(b.data?.business_id ?? null);
      })
      .catch(() => message.error("Failed to load walk-in queue"))
      .finally(() => setLoading(false));
  }, []);

  async function advance(id: string) {
    try {
      await axiosClient.patch(`/walkins/${id}/advance`);
      fetchQueue();
    } catch {
      message.error("Failed to update status");
    }
  }

  async function remove(id: string) {
    try {
      await axiosClient.delete(`/walkins/${id}`);
      fetchQueue();
    } catch {
      message.error("Failed to remove");
    }
  }

  const totals = useMemo(() => ({
    total: walkins.length,
    waiting: walkins.filter((w) => w.status === "waiting").length,
    inService: walkins.filter((w) => w.status === "in_service").length,
    served: walkins.filter((w) => w.status === "served").length,
  }), [walkins]);

  const staffName = (id?: string | null) => staff.find((s) => s.user_id === id)?.name ?? "—";

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Monitor size={18} color="#4f46e5" />
          </div>
          <div>
            <Title level={3} style={{ margin: 0 }}>Walk-In Kiosk</Title>
            <Text type="secondary" style={{ fontSize: 12 }}>{dayjs().format("dddd, MMMM D")}</Text>
          </div>
        </div>
        <Button type="primary" icon={<Play size={14} />} onClick={() => setKioskOpen(true)}>
          Check In a Walk-In
        </Button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <Card size="small"><Space size={8}><Users size={14} color="#4f46e5" /><div><div style={{ fontSize: 18, fontWeight: 700 }}>{totals.total}</div><Text type="secondary" style={{ fontSize: 11 }}>Today's Walk-ins</Text></div></Space></Card>
        <Card size="small"><Space size={8}><Clock size={14} color="#d97706" /><div><div style={{ fontSize: 18, fontWeight: 700 }}>{totals.waiting}</div><Text type="secondary" style={{ fontSize: 11 }}>Waiting</Text></div></Space></Card>
        <Card size="small"><Space size={8}><Scissors size={14} color="#2563eb" /><div><div style={{ fontSize: 18, fontWeight: 700 }}>{totals.inService}</div><Text type="secondary" style={{ fontSize: 11 }}>In Service</Text></div></Space></Card>
        <Card size="small"><Space size={8}><CheckCheck size={14} color="#059669" /><div><div style={{ fontSize: 18, fontWeight: 700 }}>{totals.served}</div><Text type="secondary" style={{ fontSize: 11 }}>Served</Text></div></Space></Card>
      </div>

      <Card size="small" title="Live Queue" loading={loading}>
        {walkins.length === 0 ? (
          <Empty description="No walk-ins yet today" />
        ) : (
          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            {walkins.map((wk, idx) => {
              const meta = STATUS_META[wk.status];
              return (
                <div key={wk.walkin_id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: idx < walkins.length - 1 ? "1px solid #f0f0f0" : undefined }}>
                  <Text type="secondary" style={{ width: 20 }}>{idx + 1}</Text>
                  <div style={{ flex: 1 }}>
                    <Text strong>{wk.client_name}</Text>
                    <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                      {wk.service_name} · {staffName(wk.staff_id)} · checked in {dayjs(wk.checked_in_at).format("h:mm A")}
                    </div>
                  </div>
                  <Tag color={meta.color}>{meta.label}</Tag>
                  {wk.status !== "served" && (
                    <Button size="small" onClick={() => advance(wk.walkin_id)}>Advance</Button>
                  )}
                  <Button size="small" type="text" danger icon={<XCircle size={14} />} onClick={() => remove(wk.walkin_id)} />
                </div>
              );
            })}
          </Space>
        )}
      </Card>

      {kioskOpen && businessId && (
        <KioskFlow
          businessId={businessId}
          services={services}
          staff={staff}
          clients={clients}
          onExit={() => setKioskOpen(false)}
          onCheckedIn={fetchQueue}
        />
      )}
    </div>
  );
}
